import { useEffect, useMemo, useState } from "react";
import api, { rupiah } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSpreadsheet } from "lucide-react";

const ID_MONTHS = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];
const monthLabel = (ym) => { if (!ym) return ""; const [y,m] = ym.split("-").map(Number); return `${ID_MONTHS[m-1]||m} ${y}`; };

export default function Reports() {
  const [period, setPeriod] = useState("month");
  const [month, setMonth] = useState("");
  const [r, setR] = useState(null);
  const [monthsAvailable, setMonthsAvailable] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((res) => setMonthsAvailable(res.data.months_available || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const params = { period };
    if (period === "month" && month) params.month = month;
    api.get("/reports", { params }).then((res) => setR(res.data));
  }, [period, month]);

  const exportHref = useMemo(() => {
    const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/api/reports/export.xlsx`);
    url.searchParams.set("period", period);
    if (period === "month" && month) url.searchParams.set("month", month);
    url.searchParams.set("token", localStorage.getItem("gg_token") || "");
    return url.toString();
  }, [period, month]);

  if (!r) return <div className="text-slate-400">Memuat laporan...</div>;

  const chartAirlines = r.airlines.map(([name, count]) => ({ name, count }));
  const chartCustomers = r.customers.map(([name, amount]) => ({ name: name.slice(0,14), amount }));
  const currentMonth = new Date().toISOString().slice(0, 7);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Reports</h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">
            Laporan penjualan, profit, dan performa OTA/airline
            {period === "month" && <> — <b>{monthLabel(month || currentMonth)}</b></>}.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList data-testid="report-tabs">
              <TabsTrigger value="day">Hari</TabsTrigger>
              <TabsTrigger value="week">Minggu</TabsTrigger>
              <TabsTrigger value="month">Bulan</TabsTrigger>
              <TabsTrigger value="year">Tahun</TabsTrigger>
            </TabsList>
          </Tabs>
          {period === "month" && (
            <Select value={month || currentMonth} onValueChange={(v) => setMonth(v === currentMonth ? "" : v)}>
              <SelectTrigger className="w-[190px] bg-white" data-testid="reports-month-picker">
                <SelectValue placeholder="Pilih bulan" />
              </SelectTrigger>
              <SelectContent>
                {(monthsAvailable.length ? monthsAvailable : [currentMonth]).map((m) => (
                  <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <a href={exportHref} target="_blank" rel="noreferrer" data-testid="report-export-btn">
            <Button className="bg-[#556b2f] hover:bg-[#3d4d1e]"><FileSpreadsheet size={14} className="mr-1"/> Export Excel</Button>
          </a>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/backup/export.zip?token=${localStorage.getItem("gg_token")}`} target="_blank" rel="noreferrer" data-testid="backup-export-btn">
            <Button variant="outline">Backup ZIP</Button>
          </a>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="stat-card"><div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Penjualan</div><div className="text-2xl font-extrabold mono mt-2 text-[#556b2f]">{rupiah(r.sales)}</div></div>
        <div className="stat-card"><div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Beli OTA</div><div className="text-2xl font-extrabold mono mt-2 text-amber-700">{rupiah(r.ota_buy_total || 0)}</div></div>
        <div className="stat-card"><div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Service Fee</div><div className="text-2xl font-extrabold mono mt-2 text-[#556b2f]">{rupiah(r.service_fee_total || 0)}</div></div>
        <div className="stat-card"><div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Gross Margin</div><div className="text-2xl font-extrabold mono mt-2 text-[#6b8e23]">{rupiah(r.gross_margin || 0)}</div></div>
        <div className="stat-card"><div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Net Profit</div><div className="text-2xl font-extrabold mono mt-2 text-[#556b2f]">{rupiah(r.profit)}</div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Airline Terlaris</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartAirlines}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3"/>
                  <XAxis dataKey="name" fontSize={11}/>
                  <YAxis fontSize={11}/>
                  <Tooltip/>
                  <Bar dataKey="count" fill="#556b2f" radius={[6,6,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Top Customers</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartCustomers} layout="vertical">
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3"/>
                  <XAxis type="number" fontSize={11} tickFormatter={(v) => (v > 999999 ? (v/1e6).toFixed(1)+"M" : v)}/>
                  <YAxis type="category" dataKey="name" fontSize={11} width={90}/>
                  <Tooltip formatter={(v) => rupiah(v)}/>
                  <Bar dataKey="amount" fill="#6b8e23" radius={[0,6,6,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>OTA Terbanyak</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {r.ota.map(([name, count]) => (
              <div key={name} className="border rounded-lg p-4 bg-slate-50">
                <div className="text-xs text-slate-500">{name}</div>
                <div className="text-xl font-bold mt-1 mono">{count}</div>
              </div>
            ))}
            {r.ota.length === 0 && <div className="text-slate-400 text-sm">Belum ada data.</div>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
